import React, { useEffect } from 'react';
import { motion, useAnimation } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import './LegalStyles.scss';
import { InfoService } from '../../../services/site/InfoService';

// Animation variants
const fadeInUp = {
  hidden: { opacity: 0, y: 40 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.5 } }
};

const staggerContainer = {
  hidden: { opacity: 0 },
  visible: { opacity: 1, transition: { staggerChildren: 0.1 } }
};

// Animated section wrapper
const AnimatedSection: React.FC<{ 
  children: React.ReactNode; 
  className?: string;
}> = ({ children, className = '' }) => {
  const controls = useAnimation();
  const [ref, inView] = useInView({ threshold: 0.1, triggerOnce: true });

  useEffect(() => {
    if (inView) {
      controls.start('visible');
    }
  }, [controls, inView]);

  return (
    <motion.div
      ref={ref}
      initial="hidden"
      animate={controls}
      variants={fadeInUp}
      className={className}
    >
      {children}
    </motion.div>
  );
};

// ============================================
// COOKIE POLICY COMPONENT (COMPLETED)
// ============================================
export const CookiePolicy: React.FC = () => {
  return (
    <div className="sds_legal-wrapper">
      <div style={{ display: 'none' }}>
        <h1>HDT Solutions Cookie Policy</h1>
        <meta name="description" content="HDT Solutions cookie policy. Learn how we use cookies and tracking technologies." />
      </div>

      <div className="container">
        <AnimatedSection className="sds_legal-header section">
          <div className="has-text-centered">
            <motion.div 
              className="sds_legal-icon"
              animate={{ rotate: [0, 360] }}
              transition={{ duration: 20, repeat: Infinity, ease: "linear" }}
            >
              🍪
            </motion.div>
            <h1 className="title is-1 sds_legal-title">Cookie Policy</h1>
            <p className="subtitle is-5 sds_legal-subtitle">
              Last Updated: {new Date().toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}
            </p>
            <p className="sds_legal-intro">
              This Cookie Policy explains how HDT Solutions uses cookies and similar tracking technologies.
            </p>
          </div>
        </AnimatedSection>

        <motion.div variants={staggerContainer} initial="hidden" animate="visible">
          <AnimatedSection className="sds_legal-section">
            <h2 className="title is-3 sds_legal-section-title">1. What Are Cookies?</h2>
            <div className="content">
              <p>Cookies are small text files stored on your device when you visit a website. They help websites:</p>
              <ul>
                <li>Remember your preferences and settings</li>
                <li>Improve website functionality and performance</li>
                <li>Provide personalized content and advertising</li>
                <li>Analyze website usage and traffic patterns</li>
              </ul>
            </div>
          </AnimatedSection>

          <AnimatedSection className="sds_legal-section">
            <h2 className="title is-3 sds_legal-section-title">2. Types of Cookies We Use</h2>
            <div className="content">
              <h3 className="title is-5">2.1 Essential Cookies</h3>
              <p>These cookies are necessary for basic website functionality:</p>
              <ul>
                <li><strong>Session Cookies:</strong> Maintain your session while navigating the site</li>
                <li><strong>Security Cookies:</strong> Authenticate users and prevent fraud</li>
                <li><strong>Load Balancing:</strong> Distribute traffic across servers</li>
              </ul>
              <p className="mt-4"><em>These cookies cannot be disabled as they are essential for service delivery.</em></p>

              <h3 className="title is-5 mt-5">2.2 Performance Cookies</h3>
              <p>These cookies help us understand how visitors interact with our website:</p>
              <ul>
                <li><strong>Analytics Cookies:</strong> Track page views, bounce rates, and user behavior</li>
                <li><strong>Error Reporting:</strong> Identify and fix technical issues</li>
                <li><strong>A/B Testing:</strong> Compare different versions of pages</li>
              </ul>
              <p className="mt-4"><em>Examples: Google Analytics, Hotjar</em></p>

              <h3 className="title is-5 mt-5">2.3 Functional Cookies</h3>
              <p>These cookies enhance functionality and personalization:</p>
              <ul>
                <li><strong>Preference Cookies:</strong> Remember language, region, and display settings</li>
                <li><strong>Customization:</strong> Store user interface preferences</li>
                <li><strong>Media Players:</strong> Remember playback settings</li>
              </ul>

              <h3 className="title is-5 mt-5">2.4 Targeting/Advertising Cookies</h3>
              <p>These cookies are used for marketing purposes:</p>
              <ul>
                <li><strong>Behavioral Advertising:</strong> Show relevant ads based on interests</li>
                <li><strong>Retargeting:</strong> Display ads to previous visitors</li>
                <li><strong>Social Media:</strong> Enable sharing and social features</li>
              </ul>
              <p className="mt-4"><em>Examples: Google Ads, Facebook Pixel, LinkedIn Insight Tag</em></p>
            </div>
          </AnimatedSection>

          <AnimatedSection className="sds_legal-section">
            <h2 className="title is-3 sds_legal-section-title">3. Cookie Duration</h2>
            <div className="content">
              <div className="table-container">
                <table className="table is-fullwidth is-striped">
                  <thead>
                    <tr>
                      <th>Cookie Type</th>
                      <th>Duration</th>
                      <th>Purpose</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td><strong>Session Cookies</strong></td>
                      <td>Until browser closes</td>
                      <td>Temporary session management</td>
                    </tr>
                    <tr>
                      <td><strong>Persistent Cookies</strong></td>
                      <td>30 days - 2 years</td>
                      <td>Remember preferences and login</td>
                    </tr>
                    <tr>
                      <td><strong>Analytics Cookies</strong></td>
                      <td>13-24 months</td>
                      <td>Track long-term usage patterns</td>
                    </tr>
                    <tr>
                      <td><strong>Advertising Cookies</strong></td>
                      <td>90 days - 1 year</td>
                      <td>Deliver relevant advertisements</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </AnimatedSection>

          <AnimatedSection className="sds_legal-section">
            <h2 className="title is-3 sds_legal-section-title">4. Third-Party Cookies</h2>
            <div className="content">
              <p>We use services from third-party providers who may set their own cookies:</p>
              <ul>
                <li><strong>Google Analytics:</strong> Website traffic and behavior analysis</li>
                <li><strong>Google Ads:</strong> Advertising and remarketing</li>
                <li><strong>Facebook Pixel:</strong> Social media advertising and tracking</li>
                <li><strong>LinkedIn Insight:</strong> Professional network advertising</li>
                <li><strong>Hotjar:</strong> Heatmaps and session recordings</li>
                <li><strong>Intercom/Zendesk:</strong> Customer support and chat</li>
              </ul>
              <div className="notification is-info is-light mt-4">
                <p><strong>Note:</strong> These third parties have their own privacy and cookie policies. We recommend reviewing their policies.</p>
              </div>
            </div>
          </AnimatedSection>

          <AnimatedSection className="sds_legal-section">
            <h2 className="title is-3 sds_legal-section-title">5. Managing Cookies</h2>
            <div className="content">
              <h3 className="title is-5">5.1 Browser Settings</h3>
              <p>You can control cookies through your browser settings:</p>
              <ul>
                <li><strong>Chrome:</strong> Settings → Privacy and Security → Cookies</li>
                <li><strong>Firefox:</strong> Options → Privacy & Security → Cookies and Site Data</li>
                <li><strong>Safari:</strong> Preferences → Privacy → Cookies and Website Data</li>
                <li><strong>Edge:</strong> Settings → Cookies and Site Permissions</li>
              </ul>

              <h3 className="title is-5 mt-5">5.2 Cookie Consent Tool</h3>
              <p>We provide a cookie consent banner on first visit. You can:</p>
              <ul>
                <li>Accept all cookies for full functionality</li>
                <li>Reject non-essential cookies</li>
                <li>Customize preferences for specific cookie types</li>
                <li>Change preferences anytime through site footer</li>
              </ul>

              <h3 className="title is-5 mt-5">5.3 Opt-Out Links</h3>
              <p>You can opt out of specific tracking services:</p>
              <ul>
                <li><a href="https://tools.google.com/dlpage/gaoptout" target="_blank" rel="noopener noreferrer">Google Analytics Opt-Out</a></li>
                <li><a href="https://optout.networkadvertising.org/" target="_blank" rel="noopener noreferrer">Network Advertising Initiative Opt-Out</a></li>
                <li><a href="https://optout.aboutads.info/" target="_blank" rel="noopener noreferrer">Digital Advertising Alliance Opt-Out</a></li>
              </ul>

              <div className="notification is-warning is-light mt-4">
                <p><strong>Important:</strong> Disabling cookies may affect website functionality. Some features may not work properly without cookies.</p>
              </div>
            </div>
          </AnimatedSection>

          <AnimatedSection className="sds_legal-section">
            <h2 className="title is-3 sds_legal-section-title">6. Other Tracking Technologies</h2>
            <div className="content">
              <p>Besides cookies, we may use other tracking technologies:</p>
              
              <h3 className="title is-5">6.1 Web Beacons (Pixels)</h3>
              <p>Small graphic images embedded in web pages or emails to track:</p>
              <ul>
                <li>Email open rates and click-throughs</li>
                <li>Page view counts</li>
                <li>Ad impression tracking</li>
              </ul>

              <h3 className="title is-5 mt-5">6.2 Local Storage</h3>
              <p>Browser storage mechanisms that retain data locally:</p>
              <ul>
                <li>HTML5 Local Storage for offline functionality</li>
                <li>Session Storage for temporary data</li>
                <li>IndexedDB for complex data structures</li>
              </ul>

              <h3 className="title is-5 mt-5">6.3 Device Fingerprinting</h3>
              <p>Collection of device and browser characteristics:</p>
              <ul>
                <li>Screen resolution and color depth</li>
                <li>Installed fonts and plugins</li>
                <li>Browser version and settings</li>
                <li>Used for fraud detection and security</li>
              </ul>
            </div>
          </AnimatedSection>

          <AnimatedSection className="sds_legal-section">
            <h2 className="title is-3 sds_legal-section-title">7. Updates to Cookie Policy</h2>
            <div className="content">
              <p>We may update this Cookie Policy to reflect:</p>
              <ul>
                <li>Changes in cookie usage or tracking technologies</li>
                <li>New legal or regulatory requirements</li>
                <li>Updates to third-party services</li>
                <li>Improvements to our privacy practices</li>
              </ul>
              <p className="mt-4">We will notify you of material changes through:</p>
              <ul>
                <li>Website banner notifications</li>
                <li>Email to registered users</li>
                <li>Updated "Last Modified" date at the top</li>
              </ul>
            </div>
          </AnimatedSection>

          <AnimatedSection className="sds_legal-contact">
            <h2 className="title is-3 sds_legal-section-title">8. Contact Us</h2>
            <div className="content">
              <p>For questions about our use of cookies or this Cookie Policy:</p>
              <div className="box sds_legal-contact-box">
                <p><strong>HDT Solutions </strong></p>
                <p>📧 Email: <a href="mailto:privacy@hdtsolution.com">privacy@hdtsolution.com</a></p>
                <p>📱 Phone: <a href="tel:+91 9553896536">{InfoService.mobile_no_1}</a></p>
                      <p>🌐 Website: <a href="https://hdtsecure.com/">hdtsecure.com</a></p>

              </div>
            </div>
          </AnimatedSection>
        </motion.div>
      </div>
    </div>
  );
};

// Demo component showing all three policies
export default function LegalPagesDemo() {
  const [activePage, setActivePage] = React.useState('cookie');

  return (
    <div>
      <div style={{ 
        position: 'sticky', 
        top: 0, 
        zIndex: 100, 
        background: '#1a1a2e', 
        padding: '1rem',
        borderBottom: '2px solid #0f3460'
      }}>
        <div style={{ 
          maxWidth: '1200px', 
          margin: '0 auto',
          display: 'flex',
          gap: '1rem',
          justifyContent: 'center',
          flexWrap: 'wrap'
        }}>
          <button 
            onClick={() => setActivePage('cookie')}
            style={{
              padding: '0.75rem 1.5rem',
              background: activePage === 'cookie' ? 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' : '#16213e',
              color: 'white',
              border: 'none',
              borderRadius: '8px',
              cursor: 'pointer',
              fontWeight: '600',
              transition: 'all 0.3s'
            }}
          >
            🍪 Cookie Policy
          </button>
        </div>
      </div>
      
      {activePage === 'cookie' && <CookiePolicy />}
    </div>
  );
}